import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Skeleton } from '@mui/material';
import { RootState } from 'store';
import AdminLayout from '../AdminLayout';
import MagicButton from 'components/MagicButton/MagicButton';
import {
  AdminEarningsParams,
  AffiliateEarningStatus,
  buildAdminEarningsQueryString,
  useGetAdminEarningsQuery,
} from 'store/services/affiliates/affiliatesApi';
import { downloadAuthedFile } from 'utils/authedDownload';
import MarkPaidModal from './MarkPaidModal';
import VoidEarningModal from './VoidEarningModal';
import { formatCents, formatShortDate, getApiErrorMessage } from './affiliateAdminUtils';
import './AdminAffiliatesPage.css';

const PAGE_SIZE = 25;

type SortableColumn = 'amount' | 'status' | 'subscriptionDate';

const STATUS_OPTIONS: { value: AffiliateEarningStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'eligible', label: 'Eligible' },
  { value: 'paid', label: 'Paid' },
  { value: 'voided', label: 'Voided' },
];

const SUMMARY_TILES: { status: AffiliateEarningStatus; label: string }[] = [
  { status: 'pending', label: 'Pending' },
  { status: 'eligible', label: 'Eligible' },
  { status: 'paid', label: 'Paid' },
  { status: 'voided', label: 'Voided' },
];

const AdminAffiliatesPage = () => {
  const token = useSelector((state: RootState) => state.auth.token);

  // Filters
  const [statuses, setStatuses] = useState<AffiliateEarningStatus[]>([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [usernameInput, setUsernameInput] = useState('');
  const [appliedUsername, setAppliedUsername] = useState('');

  // Sorting / pagination
  const [sortBy, setSortBy] = useState<AdminEarningsParams['sortBy']>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  // Selection & modals
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isMarkPaidOpen, setIsMarkPaidOpen] = useState(false);
  const [voidTargetId, setVoidTargetId] = useState<number | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Debounce the username search (~400ms); also applied on Enter/blur.
  useEffect(() => {
    if (usernameInput === appliedUsername) return undefined;
    const timer = setTimeout(() => {
      setAppliedUsername(usernameInput);
      setPage(1);
      setSelectedIds([]);
    }, 400);
    return () => clearTimeout(timer);
  }, [usernameInput, appliedUsername]);

  const applyUsernameNow = () => {
    if (usernameInput !== appliedUsername) {
      setAppliedUsername(usernameInput);
      setPage(1);
      setSelectedIds([]);
    }
  };

  const filterParams: AdminEarningsParams = useMemo(
    () => ({
      statuses: statuses.length > 0 ? statuses : undefined,
      from: fromDate || undefined,
      to: toDate || undefined,
      username: appliedUsername || undefined,
    }),
    [statuses, fromDate, toDate, appliedUsername]
  );

  const { data, isLoading, isFetching, error, refetch } = useGetAdminEarningsQuery({
    ...filterParams,
    page,
    pageSize: PAGE_SIZE,
    sortBy,
    sortDir,
  });

  const earnings = useMemo(() => data?.earnings ?? [], [data]);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Selection is only ever valid against eligible rows on the current page.
  const eligibleRowsOnPage = useMemo(
    () => earnings.filter((earning) => earning.status === 'eligible'),
    [earnings]
  );
  const selectedRows = useMemo(
    () => eligibleRowsOnPage.filter((earning) => selectedIds.includes(earning.id)),
    [eligibleRowsOnPage, selectedIds]
  );
  const allEligibleSelected =
    eligibleRowsOnPage.length > 0 &&
    eligibleRowsOnPage.every((earning) => selectedIds.includes(earning.id));

  const voidTarget = voidTargetId !== null
    ? earnings.find((earning) => earning.id === voidTargetId) ?? null
    : null;

  const toggleStatusFilter = (status: AffiliateEarningStatus) => {
    setStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
    setPage(1);
    setSelectedIds([]);
  };

  const handleFromDateChange = (value: string) => {
    setFromDate(value);
    setPage(1);
    setSelectedIds([]);
  };

  const handleToDateChange = (value: string) => {
    setToDate(value);
    setPage(1);
    setSelectedIds([]);
  };

  const handleSort = (column: SortableColumn) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
    setPage(1);
    setSelectedIds([]);
  };

  const getSortArrow = (column: SortableColumn) => {
    if (sortBy !== column) return null;
    return (
      <span className="admin-affil-sort-arrow">{sortDir === 'asc' ? '▲' : '▼'}</span>
    );
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    setSelectedIds([]);
  };

  const toggleRowSelection = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleSelectAllEligible = () => {
    if (allEligibleSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(eligibleRowsOnPage.map((earning) => earning.id));
    }
  };

  const handleExportCsv = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const queryString = buildAdminEarningsQueryString({
        ...filterParams,
        sortBy,
        sortDir,
      });
      await downloadAuthedFile(
        `/admin/affiliates/earnings/export.csv?${queryString}`,
        'partner-earnings.csv',
        token
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to export CSV. Please try again.'));
    } finally {
      setIsExporting(false);
    }
  };

  const getStatusBadgeClass = (status: AffiliateEarningStatus) => {
    switch (status) {
      case 'pending':
        return 'admin-affil-badge-pending';
      case 'eligible':
        return 'admin-affil-badge-eligible';
      case 'paid':
        return 'admin-affil-badge-paid';
      case 'voided':
        return 'admin-affil-badge-voided';
      default:
        return '';
    }
  };

  const hasActiveFilters =
    statuses.length > 0 || fromDate !== '' || toDate !== '' || appliedUsername !== '';

  return (
    <AdminLayout title="Admin Panel - Partner Program" currentPath="/admin/affiliates">
      <div className="admin-affil-page">
        <div className="admin-affil-header">
          <h2 className="admin-affil-title">Partner Program Earnings</h2>
          <p className="admin-affil-subtitle">
            Review, pay out, and void partner referral earnings
          </p>
        </div>

        {/* Summary tiles (current UTC month) */}
        <div className="admin-affil-summary-row">
          {SUMMARY_TILES.map((tile) => {
            const bucket = data?.summary[tile.status];
            return (
              <div
                key={tile.status}
                className={`admin-affil-summary-tile admin-affil-tile-${tile.status}`}
              >
                <span className="admin-affil-tile-label">{tile.label} (This Month)</span>
                {bucket ? (
                  <>
                    <span className="admin-affil-tile-amount">
                      {formatCents(bucket.totalCents)}
                    </span>
                    <span className="admin-affil-tile-count">
                      {bucket.count} earning{bucket.count === 1 ? '' : 's'}
                    </span>
                  </>
                ) : (
                  <>
                    <Skeleton
                      variant="text"
                      width={90}
                      height={32}
                      sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }}
                    />
                    <Skeleton
                      variant="text"
                      width={70}
                      height={18}
                      sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }}
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="admin-affil-filters">
          <div className="admin-affil-status-pills">
            {STATUS_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`admin-affil-status-pill ${
                  statuses.includes(option.value) ? 'active' : ''
                }`}
                onClick={() => toggleStatusFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="admin-affil-date-filters">
            <label className="admin-affil-date-label">
              From
              <input
                type="date"
                className="admin-affil-date-input"
                value={fromDate}
                onChange={(e) => handleFromDateChange(e.target.value)}
              />
            </label>
            <label className="admin-affil-date-label">
              To
              <input
                type="date"
                className="admin-affil-date-input"
                value={toDate}
                onChange={(e) => handleToDateChange(e.target.value)}
              />
            </label>
          </div>

          <input
            type="text"
            className="admin-affil-search-input"
            placeholder="Search by partner username"
            value={usernameInput}
            onChange={(e) => setUsernameInput(e.target.value)}
            onBlur={applyUsernameNow}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyUsernameNow();
            }}
          />
        </div>

        {/* Toolbar */}
        <div className="admin-affil-toolbar">
          <MagicButton
            label={`Mark Selected as Paid${selectedRows.length > 0 ? ` (${selectedRows.length})` : ''}`}
            color="purple"
            onPress={() => setIsMarkPaidOpen(true)}
            isDisabled={selectedRows.length === 0}
          />
          <MagicButton
            label={isExporting ? 'Exporting...' : 'Export CSV'}
            color="grey"
            onPress={handleExportCsv}
            isLoading={isExporting}
            isDisabled={isExporting}
          />
        </div>

        {/* Table */}
        <div className="admin-affil-content">
          {error ? (
            <div className="admin-affil-error-state">
              <h3 className="admin-affil-error-title">Unable to Load Earnings</h3>
              <p className="admin-affil-error-message">
                Something went wrong while loading partner earnings. Please try again.
              </p>
              <MagicButton label="Try Again" color="purple" onPress={() => refetch()} />
            </div>
          ) : (
            <div className="admin-affil-table-scroll">
              <div className={`admin-affil-table ${isFetching ? 'admin-affil-table-fetching' : ''}`}>
                <div className="admin-affil-table-header">
                  <div className="admin-affil-col-checkbox">
                    <input
                      type="checkbox"
                      className="admin-affil-checkbox"
                      checked={allEligibleSelected}
                      onChange={toggleSelectAllEligible}
                      disabled={eligibleRowsOnPage.length === 0}
                      aria-label="Select all eligible earnings on this page"
                    />
                  </div>
                  <div className="admin-affil-col-affiliate">Affiliate</div>
                  <div className="admin-affil-col-paypal">PayPal Email</div>
                  <div className="admin-affil-col-referred">Referred User</div>
                  <button
                    type="button"
                    className="admin-affil-col-subdate admin-affil-sortable"
                    onClick={() => handleSort('subscriptionDate')}
                  >
                    Subscription Date{getSortArrow('subscriptionDate')}
                  </button>
                  <div className="admin-affil-col-stripe">Stripe Charge</div>
                  <button
                    type="button"
                    className="admin-affil-col-status admin-affil-sortable"
                    onClick={() => handleSort('status')}
                  >
                    Status{getSortArrow('status')}
                  </button>
                  <button
                    type="button"
                    className="admin-affil-col-amount admin-affil-sortable"
                    onClick={() => handleSort('amount')}
                  >
                    Amount{getSortArrow('amount')}
                  </button>
                  <div className="admin-affil-col-actions">Actions</div>
                </div>

                {isLoading ? (
                  <div className="admin-affil-loading">
                    {[...Array(8)].map((_, index) => (
                      <div key={index} className="admin-affil-row-skeleton">
                        <Skeleton
                          variant="text"
                          width="100%"
                          height={22}
                          sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }}
                        />
                      </div>
                    ))}
                  </div>
                ) : earnings.length === 0 ? (
                  <div className="admin-affil-empty-state">
                    <h3 className="admin-affil-empty-title">No Earnings Found</h3>
                    <p className="admin-affil-empty-description">
                      {hasActiveFilters
                        ? 'No partner earnings match your filters. Try adjusting or clearing them.'
                        : 'There are no partner earnings in the system yet.'}
                    </p>
                  </div>
                ) : (
                  <div className="admin-affil-table-body">
                    {earnings.map((earning) => {
                      const isSelectable = earning.status === 'eligible';
                      const isVoidable =
                        earning.status === 'pending' || earning.status === 'eligible';
                      return (
                        <div key={earning.id} className="admin-affil-row">
                          <div className="admin-affil-col-checkbox">
                            <input
                              type="checkbox"
                              className="admin-affil-checkbox"
                              checked={isSelectable && selectedIds.includes(earning.id)}
                              onChange={() => toggleRowSelection(earning.id)}
                              disabled={!isSelectable}
                              aria-label={`Select earning ${earning.id}`}
                            />
                          </div>
                          <div className="admin-affil-col-affiliate">
                            <Link
                              to={`/profile/u/${earning.affiliateUsername}`}
                              className="admin-affil-user-link"
                            >
                              {earning.affiliateUsername}
                            </Link>
                          </div>
                          <div className="admin-affil-col-paypal">
                            {earning.paypalEmail ?? '—'}
                          </div>
                          <div className="admin-affil-col-referred">
                            <Link
                              to={`/profile/u/${earning.referredUsername}`}
                              className="admin-affil-user-link"
                            >
                              {earning.referredUsername}
                            </Link>
                          </div>
                          <div className="admin-affil-col-subdate">
                            {formatShortDate(earning.subscriptionDate)}
                          </div>
                          <div className="admin-affil-col-stripe">
                            {earning.stripeChargeId ? (
                              <a
                                href={`https://dashboard.stripe.com/payments/${earning.stripeChargeId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="admin-affil-stripe-link"
                                title={earning.stripeChargeId}
                              >
                                {earning.stripeChargeId}
                              </a>
                            ) : (
                              '—'
                            )}
                          </div>
                          <div className="admin-affil-col-status">
                            <span
                              className={`admin-affil-badge ${getStatusBadgeClass(earning.status)}`}
                            >
                              {earning.status}
                            </span>
                          </div>
                          <div className="admin-affil-col-amount">
                            {formatCents(earning.amountCents)}
                          </div>
                          <div className="admin-affil-col-actions">
                            <button
                              type="button"
                              className="admin-affil-void-button"
                              onClick={() => setVoidTargetId(earning.id)}
                              disabled={!isVoidable}
                            >
                              Void
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pagination */}
          {!error && !isLoading && total > 0 && (
            <div className="admin-affil-pagination">
              <MagicButton
                label="Previous"
                color="grey"
                onPress={() => handlePageChange(page - 1)}
                isDisabled={page <= 1}
              />
              <span className="admin-affil-pagination-info">
                Page {page} of {totalPages} ({total} total)
              </span>
              <MagicButton
                label="Next"
                color="grey"
                onPress={() => handlePageChange(page + 1)}
                isDisabled={page >= totalPages}
              />
            </div>
          )}
        </div>
      </div>

      <MarkPaidModal
        isOpen={isMarkPaidOpen}
        selectedRows={selectedRows}
        onClose={() => setIsMarkPaidOpen(false)}
        onSuccess={() => setSelectedIds([])}
      />
      <VoidEarningModal earning={voidTarget} onClose={() => setVoidTargetId(null)} />
    </AdminLayout>
  );
};

export default AdminAffiliatesPage;
